import HelpTip from '../../../components/HelpTip';
import { deleteAssetConfigRefImage, uploadAssetConfigRefImage } from '../../../api';
import { tosThumbnail } from '../../../utils/tosImage';
import { MAX_TOTAL_REF_IMAGES } from '../utils/editDefaults';
import { useImageUploadZone } from '../../../hooks/useImageUploadZone';
import { useImageAspectRatioGuard } from '../../../hooks/useImageAspectRatioGuard';
import { getDropHighlightStyle } from '../../../utils/imageFiles';

type EntityType = 'character' | 'scene' | 'prop';

interface BaseImageConfigSectionProps {
  projectId?: string;
  entityType: EntityType;
  title: string;
  titleTip: string;
  fixedPromptLabel: string;
  fixedPromptPlaceholder: string;
  projectRefImagesLabel: string;
  fixedPrompt: string;
  projectRefImages: string[];
  onFixedPromptChange: (prompt: string) => void;
  onProjectRefImagesChange: (images: string[]) => void;
}

export default function BaseImageConfigSection({
  projectId,
  entityType,
  title,
  titleTip,
  fixedPromptLabel,
  fixedPromptPlaceholder,
  projectRefImagesLabel,
  fixedPrompt,
  projectRefImages,
  onFixedPromptChange,
  onProjectRefImagesChange,
}: BaseImageConfigSectionProps) {
  const maxProjectRefs = MAX_TOTAL_REF_IMAGES;
  const guardAspectRatio = useImageAspectRatioGuard();

  const uploadConfigRefFile = async (file: File) => {
    if (!projectId) return;
    if (!(await guardAspectRatio(file))) return;
    try {
      const res = await uploadAssetConfigRefImage(projectId, entityType, file);
      onProjectRefImagesChange(res.projectRefImages);
    } catch {}
  };

  const zone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, maxProjectRefs - projectRefImages.length),
    onFiles: async imgs => {
      for (const f of imgs) await uploadConfigRefFile(f);
    },
  });

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
      <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-2)' }}>
        {title}
        <HelpTip text={titleTip} />
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div>
          <label style={{ fontSize: '0.82rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.25rem' }}>{fixedPromptLabel}</label>
          <textarea
            value={fixedPrompt}
            onChange={event => onFixedPromptChange(event.target.value)}
            rows={2}
            style={{ width: '100%', fontSize: '0.82rem', border: '1px solid var(--border)', borderRadius: '4px', padding: '4px 8px', resize: 'vertical', boxSizing: 'border-box' }}
            placeholder={fixedPromptPlaceholder}
          />
        </div>
        <div>
          <label style={{ fontSize: '0.82rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.25rem' }}>
            {projectRefImagesLabel}
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {projectRefImages.map((url, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <img src={tosThumbnail(url, 52)} alt="" style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)', display: 'block' }} />
                <button
                  type="button"
                  onClick={async () => {
                    if (!projectId) return;
                    try {
                      const res = await deleteAssetConfigRefImage(projectId, entityType, url);
                      onProjectRefImagesChange(res.projectRefImages);
                    } catch {}
                  }}
                  style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', background: 'var(--danger)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                >
                  x
                </button>
              </div>
            ))}
            {projectRefImages.length < maxProjectRefs && (
              <label {...zone.zoneProps} style={{ width: '52px', height: '52px', border: '1px dashed var(--border)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-3)', fontSize: '1.2rem', ...getDropHighlightStyle(zone.isOver) }}>
                +
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={event => {
                    const file = event.target.files?.[0];
                    if (file) uploadConfigRefFile(file);
                    event.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
